/**
 * MO.Slider — Lightweight CSS scroll-snap slider (Swiper replacement)
 *
 * Usage:
 *   var slider = new MO.Slider(containerEl, {
 *     slidesPerView: 1.2,
 *     spaceBetween: 8,
 *     pagination: true,
 *   });
 *   slider.slideTo(0);
 *   slider.update();    // recalculate after DOM changes
 *   slider.destroy();   // cleanup
 */
(function () {
  "use strict";

  var MO = window.MO || (window.MO = {});

  function Slider(container, opts) {
    if (!container) return;
    this._container = container;
    this._opts = Object.assign(
      {
        slidesPerView: 1,
        slidesPerGroup: 1,
        spaceBetween: 0,
        pagination: false,
      },
      opts || {}
    );

    this._track = null;
    this._slides = [];
    this._paginationEl = null;
    this._bullets = [];
    this._activeIndex = 0;
    this._scrollTimer = null;
    this._onScroll = this._handleScroll.bind(this);
    this._resizeObserver = null;

    this._build();
  }

  Slider.prototype._build = function () {
    var self = this;

    // Ensure container has slider class
    this._container.classList.add("mo-slider");

    // Find or create track
    this._track = this._container.querySelector(".mo-slider__track");
    if (!this._track) {
      this._track = document.createElement("div");
      this._track.className = "mo-slider__track";
      // Move existing children into track
      while (this._container.firstChild) {
        this._track.appendChild(this._container.firstChild);
      }
      this._container.appendChild(this._track);
    }

    this._applySlideStyles();
    this._track.addEventListener("scroll", this._onScroll, { passive: true });

    // Pagination
    if (this._opts.pagination) {
      this._buildPagination();
    }

    // Observe resize
    if (typeof ResizeObserver !== "undefined") {
      this._resizeObserver = new ResizeObserver(function () {
        self._applySlideStyles();
        if (self._opts.pagination) self._buildPagination();
      });
      this._resizeObserver.observe(this._container);
    }
  };

  Slider.prototype._applySlideStyles = function () {
    var slides = this._track.querySelectorAll(".mo-slider__slide");
    this._slides = Array.prototype.slice.call(slides);
    if (this._slides.length === 0) return;

    var gap = this._opts.spaceBetween;
    var spv = this._opts.slidesPerView;
    var containerW = this._container.offsetWidth;

    // width = (containerWidth - (spv-1)*gap) / spv for full slides
    // For fractional slidesPerView (like 1.2), the partial slide peeks
    var slideW = (containerW - (Math.floor(spv) - 1) * gap) / spv;

    for (var i = 0; i < this._slides.length; i++) {
      this._slides[i].style.width = slideW + "px";
      this._slides[i].style.minWidth = slideW + "px";
      if (i < this._slides.length - 1) {
        this._slides[i].style.marginRight = gap + "px";
      } else {
        this._slides[i].style.marginRight = "0";
      }
    }

    this._track.style.gap = "0";
  };

  Slider.prototype._buildPagination = function () {
    // Remove old pagination
    if (this._paginationEl) {
      this._paginationEl.remove();
    }

    var totalPages = this._getTotalPages();
    if (totalPages <= 1) return;

    this._paginationEl = document.createElement("div");
    this._paginationEl.className = "mo-slider__pagination";

    // Calculate --width CSS variable like original Swiper implementation
    var bulletWidth = "calc(100% / " + totalPages + ")";

    this._bullets = [];
    for (var i = 0; i < totalPages; i++) {
      var bullet = document.createElement("button");
      bullet.className = "mo-slider__bullet";
      bullet.type = "button";
      bullet.style.setProperty("--width", bulletWidth);
      bullet.setAttribute("aria-label", "Go to slide " + (i + 1));
      if (i === this._activeIndex) bullet.classList.add("active");
      bullet.addEventListener("click", this._onBulletClick.bind(this, i));
      this._paginationEl.appendChild(bullet);
      this._bullets.push(bullet);
    }

    this._container.appendChild(this._paginationEl);
  };

  Slider.prototype._getTotalPages = function () {
    var spg = this._opts.slidesPerGroup;
    return Math.ceil(this._slides.length / spg);
  };

  Slider.prototype._handleScroll = function () {
    var self = this;
    if (this._scrollTimer) clearTimeout(this._scrollTimer);
    this._scrollTimer = setTimeout(function () {
      self._updateActiveIndex();
    }, 50);
  };

  Slider.prototype._updateActiveIndex = function () {
    if (this._slides.length === 0) return;
    var scrollLeft = this._track.scrollLeft;
    var spg = this._opts.slidesPerGroup;
    var slideW = this._slides[0].offsetWidth + this._opts.spaceBetween;
    var rawIndex = Math.round(scrollLeft / (slideW * spg));
    var totalPages = this._getTotalPages();
    var newIndex = Math.max(0, Math.min(rawIndex, totalPages - 1));

    if (newIndex !== this._activeIndex) {
      this._activeIndex = newIndex;
      this._updateBullets();
    }
  };

  Slider.prototype._updateBullets = function () {
    for (var i = 0; i < this._bullets.length; i++) {
      if (i === this._activeIndex) {
        this._bullets[i].classList.add("active");
      } else {
        this._bullets[i].classList.remove("active");
      }
    }
  };

  Slider.prototype._onBulletClick = function (pageIndex) {
    this.slideTo(pageIndex * this._opts.slidesPerGroup);
  };

  // ── Public API ──────────────────────────────────────────────

  Slider.prototype.slideTo = function (slideIndex) {
    if (this._slides.length === 0) return;
    var idx = Math.max(0, Math.min(slideIndex, this._slides.length - 1));
    var slideW = this._slides[0].offsetWidth + this._opts.spaceBetween;
    this._track.scrollTo({ left: slideW * idx, behavior: "smooth" });
  };

  Slider.prototype.update = function () {
    this._applySlideStyles();
    if (this._opts.pagination) this._buildPagination();
    this._activeIndex = 0;
  };

  Slider.prototype.destroy = function () {
    if (this._track) {
      this._track.removeEventListener("scroll", this._onScroll);
    }
    if (this._paginationEl) {
      this._paginationEl.remove();
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    this._slides = [];
    this._bullets = [];
  };

  // Expose snapGrid-like property for external gallery pagination
  Object.defineProperty(Slider.prototype, "snapGrid", {
    get: function () {
      var totalPages = this._getTotalPages();
      var arr = [];
      for (var i = 0; i < totalPages; i++) arr.push(i);
      return arr;
    },
  });

  MO.Slider = Slider;
})();
